// ============================================
// GANTT EXPORT — PDF (landscape, logo + watermark) and Excel
// ============================================
// Uses jsPDF + jspdf-autotable (already in the project, same as ExportModal).
// The PDF draws weekly timeline columns coloured by task status, so it reads
// like the on-screen Gantt. Excel is a formatted .xls grid (opens straight in
// Excel with colours — no CSV).

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getCompanyDetails } from '../config/CompanyDetails';

// Bars are coloured by status · priority · phase (the choice made on the Gantt).
// "Not started" is brand navy — a grey bar reads as "nothing here".
const STATUS_COLOR = {
  not_started: [10, 25, 41],
  in_progress: [37, 99, 235],
  done: [0, 168, 107],
  blocked: [239, 68, 68]
};
const STATUS_LABEL = {
  not_started: 'Not started',
  in_progress: 'In progress',
  done: 'Done',
  blocked: 'Blocked'
};
const PRIORITY_LABEL = { urgent: 'Urgent', high: 'High', moderate: 'Moderate', low: 'Low' };
const PRIORITY_COLOR = {
  urgent: [239, 68, 68],
  high: [249, 115, 22],
  moderate: [234, 179, 8],
  low: [59, 130, 246]
};
const PHASE_COLORS = [
  [10, 25, 41], [14, 116, 144], [124, 58, 237], [180, 83, 9],
  [15, 118, 110], [190, 18, 60], [67, 56, 202], [77, 124, 15]
];
const NO_PHASE_COLOR = [100, 116, 139];
const BRAND_GREEN = [0, 168, 107];

// jsPDF's built-in fonts are WinAnsi, so glyphs like ◆ print as mojibake
// ("%Æ"). Strip anything the font can't draw — the ◆ marker is DRAWN instead.
const pdfSafe = (v) => String(v ?? '')
  .replace(/[\u2190-\u21FF\u2500-\u27BF\u2B00-\u2BFF]/g, '')
  .replace(/[\uD800-\uDFFF]/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const toDate = (s) => (s ? new Date(`${String(s).slice(0, 10)}T00:00:00Z`) : null);
const fmt = (d) => (d ? d.toISOString().slice(0, 10) : null);
const addDays = (s, n) => {
  const d = toDate(s);
  if (!d) return s;
  d.setUTCDate(d.getUTCDate() + n);
  return fmt(d);
};
const diffDays = (a, b) => Math.round((toDate(b) - toDate(a)) / 86400000);
const pretty = (s) => (s ? toDate(s).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }) : 'TBC');

// Absolute URL like the other PDFs use (resolveLogo in ClientInvoicePDF/QuotePDF)
const toAbsolute = (p) => {
  if (!p) return null;
  if (p.startsWith('http://') || p.startsWith('https://') || p.startsWith('data:')) return p;
  return typeof window !== 'undefined' ? window.location.origin + p : p;
};

// Load through an <img> + canvas: normalises big/odd PNGs and keeps the PDF small.
const loadImageDataUrl = (url) => new Promise((resolve) => {
  const abs = toAbsolute(url);
  if (!abs) { resolve(null); return; }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      const maxW = 1000; // scale big logos down so the PDF stays lean
      const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve({ dataUrl: canvas.toDataURL('image/png'), format: 'PNG', width: canvas.width, height: canvas.height });
    } catch (e) {
      console.warn('Logo could not be converted:', e?.message);
      resolve(null);
    }
  };
  img.onerror = () => resolve(null);
  img.src = abs;
});

// Try the preferred logo first, then the configured one and the other files.
const LOGO_PREFERRED = '/Images/One_Purpose_Logo.png';
const loadLogo = async (configured) => {
  const candidates = [
    LOGO_PREFERRED,
    configured,
    '/Images/One Purpose Header Invoice 2.png',
    '/Images/OneViewLogo clear background.png',
    '/Images/One Purpose Header Invoice.png'
  ].filter(Boolean);
  for (const c of candidates) {
    if (c === configured && configured === LOGO_PREFERRED) continue;
    const img = await loadImageDataUrl(c);
    if (img) return img;
  }
  return null;
};

// ── milestones & stats ──
const isMilestone = (t) => !!(t.start_date && !t.end_date) || !!(!t.start_date && t.end_date);
const milestoneDate = (t) => t.start_date || t.end_date || null;

const statusCounts = (tasks) => {
  const c = { not_started: 0, in_progress: 0, done: 0, blocked: 0 };
  tasks.forEach((t) => { c[t.status || 'not_started'] = (c[t.status || 'not_started'] || 0) + 1; });
  return c;
};

const ownerLabel = (t) => {
  const a = t.assignees || [];
  if (!a.length) return 'Unallocated';
  const first = a[0].name || a[0].email || 'Person';
  return a.length > 1 ? `${first} +${a.length - 1}` : first;
};

// Phases first, then their tasks; anything without a phase goes last.
const groupTasks = (phases, tasks) => {
  const out = [];
  phases.forEach((p) => {
    const list = tasks.filter((t) => t.phase_id === p.id);
    if (list.length) out.push({ id: p.id, name: p.name, tasks: list });
  });
  const orphans = tasks.filter((t) => !t.phase_id || !phases.some((p) => p.id === t.phase_id));
  if (orphans.length) out.push({ id: '__none', name: 'General', tasks: orphans });
  return out;
};

// ── Where does the calendar start & end? ────────────────────────────────────
// The PROJECT's own dates define the calendar (that's the plan the user sees),
// extended only if a task/phase actually runs outside them, so no bar is lost.
const rawSpan = (project, tasks, phases) => {
  const all = [];
  tasks.forEach((t) => { if (t.start_date) all.push(String(t.start_date).slice(0, 10)); if (t.end_date) all.push(String(t.end_date).slice(0, 10)); });
  phases.forEach((p) => { if (p.start_date) all.push(String(p.start_date).slice(0, 10)); if (p.end_date) all.push(String(p.end_date).slice(0, 10)); });
  if (project?.start_date) all.push(String(project.start_date).slice(0, 10));
  if (project?.end_date) all.push(String(project.end_date).slice(0, 10));
  if (!all.length) return null;
  return { min: all.reduce((a, b) => (a < b ? a : b)), max: all.reduce((a, b) => (a > b ? a : b)) };
};

// How much detail fits across the page? Short jobs get a column PER DAY (the
// user's ask), medium ones per week, long ones per month.
const DAY_LIMIT = 70;   // ≤ 70 days  → one column per day
const WEEK_LIMIT = 400; // ≤ 400 days → per week, otherwise per month
const granularityFor = (raw) => {
  if (!raw) return 'day';
  const days = diffDays(raw.min, raw.max) + 1;
  if (days <= DAY_LIMIT) return 'day';
  if (days <= WEEK_LIMIT) return 'week';
  return 'month';
};

// Align the span to the granularity (no padding for days — show them all).
const alignSpan = (raw, gran) => {
  if (!raw) return null;
  if (gran === 'day') return { start: raw.min, end: raw.max };
  if (gran === 'week') {
    const dow = (toDate(raw.min).getUTCDay() + 6) % 7; // Mon = 0
    return { start: addDays(raw.min, -dow), end: addDays(raw.max, 6) };
  }
  const s = toDate(raw.min);
  const e = toDate(raw.max);
  return {
    start: fmt(new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), 1))),
    end: fmt(new Date(Date.UTC(e.getUTCFullYear(), e.getUTCMonth() + 1, 0)))
  };
};

// One entry per calendar column: { start, end }
const dayBuckets = (span) => {
  if (!span) return [];
  const total = diffDays(span.start, span.end) + 1;
  const out = [];
  for (let i = 0; i < total; i++) { const d = addDays(span.start, i); out.push({ start: d, end: d }); }
  return out;
};

const weekBuckets = (span) => {
  if (!span) return [];
  const weeks = Math.max(Math.ceil((diffDays(span.start, span.end) + 1) / 7), 1);
  const out = [];
  for (let i = 0; i < weeks; i++) {
    const s = addDays(span.start, i * 7);
    out.push({ start: s, end: addDays(s, 6) });
  }
  return out;
};

const monthBuckets = (span) => {
  if (!span) return [];
  const out = [];
  const end = toDate(span.end);
  let cur = toDate(span.start);
  cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth(), 1));
  while (cur <= end) {
    const s = fmt(cur);
    const monthEnd = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 0));
    out.push({ start: s < span.start ? span.start : s, end: fmt(monthEnd) > span.end ? span.end : fmt(monthEnd) });
    cur = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 1));
  }
  return out;
};

const bucketsOf = (span, gran) => (
  gran === 'day' ? dayBuckets(span) : gran === 'week' ? weekBuckets(span) : monthBuckets(span)
);

// Column heading: day number (with the month on the 1st / first column),
// "07 Sept" for weeks, "Sept 26" for months.
const shortMonth = (d) => d.toLocaleDateString('en-ZA', { month: 'short' });
const bucketLabel = (b, first) => {
  const d = toDate(b.start);
  if (b.start === b.end) return (d.getUTCDate() === 1 || first) ? `${d.getUTCDate()} ${shortMonth(d)}` : String(d.getUTCDate());
  if (diffDays(b.start, b.end) <= 7) return pretty(b.start);
  return `${shortMonth(d)} ${String(d.getUTCFullYear()).slice(2)}`;
};

// One calendar setup, shared by the PDF and the Excel exports.
const calendarFor = (project, phases, tasks, usableW, baseW) => {
  const raw = rawSpan(project, tasks, phases);
  const gran = granularityFor(raw);
  const span = alignSpan(raw, gran);
  const cols = bucketsOf(span, gran);
  const totalDays = span ? diffDays(span.start, span.end) + 1 : 0;
  const pxPerDay = totalDays && usableW - baseW > 0 ? (usableW - baseW) / totalDays : 0;
  const colWidths = cols.map((b) => Math.max((diffDays(b.start, b.end) + 1) * pxPerDay, 1.2));
  return { span, gran, cols, colWidths, pxPerDay, totalDays };
};

const overlaps = (task, week) => {
  const s = task.start_date || task.end_date;
  const e = task.end_date || task.start_date;
  if (!s) return false;
  return !(e < week.start || s > week.end);
};


// ============================================
// PDF — landscape A3, logo top-left, watermark behind the grid
// ============================================
export async function exportGanttPDF({ project = {}, phases = [], tasks = [], company: companyIn, colorBy = 'status' }) {
  const company = companyIn || await getCompanyDetails().catch(() => ({}));
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // ── watermark (solid light grey so it renders on every viewer) ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(96);
  doc.setTextColor(219, 226, 236);
  doc.text('ONE PURPOSE LOGISTICS', pageW / 2, pageH / 2 + 20, { align: 'center', angle: 25 });
  doc.setFontSize(40);
  doc.setTextColor(232, 237, 244);
  doc.text('PROJECT SCHEDULE', pageW / 2, pageH / 2 + 95, { align: 'center', angle: 25 });

  // ── logo (aspect-ratio aware, falls back to the company name) ──
  const logo = await loadLogo(company.company_logo);
  if (logo) {
    try {
      const maxW = 46, maxH = 15;
      const ratio = (logo.width || 1) / (logo.height || 1);
      let w = maxW;
      let h = w / ratio;
      if (h > maxH) { h = maxH; w = h * ratio; }
      doc.addImage(logo.dataUrl, logo.format, 12, 9, w, h);
    } catch (e) { console.warn('PDF logo skipped:', e?.message); }
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(10, 25, 41);
    doc.text(pdfSafe(company.company_name) || 'One Purpose Logistics', 12, 17);
  }

  // ── title block ──
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(10, 25, 41);
  doc.text(pdfSafe(project.name) || 'Project Schedule', pageW / 2, 15, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(90, 100, 115);
  const sub = [
    project.project_number ? `Project ${project.project_number}` : null,
    project.client?.company_name ? `Client: ${project.client.company_name}` : null,
    `Generated ${new Date().toLocaleDateString('en-ZA')}`
  ].filter(Boolean).join('   ·   ');
  doc.text(sub, pageW / 2, 21, { align: 'center' });

  // ── stats strip + progress bar (left, under the logo) ──
  const counts = statusCounts(tasks);
  const total = tasks.length;
  const donePct = total ? Math.round((counts.done / total) * 100) : 0;
  const milestones = tasks.filter(isMilestone).length;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(90, 100, 115);
  doc.text(
    `${total} tasks · ${donePct}% done · ${counts.in_progress} in progress · ${counts.blocked} blocked · ${milestones} milestones`,
    12, 30
  );
  // stacked progress bar
  const barX = 12, barY = 33, barW = 120, barH = 4;
  const segs = [
    ['done', [0, 168, 107]],
    ['in_progress', [37, 99, 235]],
    ['blocked', [239, 68, 68]],
    ['not_started', [203, 213, 225]]
  ];
  let x = barX;
  segs.forEach(([k, col]) => {
    const w = total ? (counts[k] / total) * barW : 0;
    if (w <= 0) return;
    doc.setFillColor(col[0], col[1], col[2]);
    doc.rect(x, barY, w, barH, 'F');
    x += w;
  });

  // ── colour key (mirrors the chosen bar colour mode) ──
  const legend = colorBy === 'brand'
    ? [['One Purpose green', BRAND_GREEN]]
    : colorBy === 'priority'
      ? Object.keys(PRIORITY_LABEL).map((k) => [PRIORITY_LABEL[k], PRIORITY_COLOR[k]])
      : colorBy === 'phase'
        ? phases.map((p, i) => [String(p.name || 'Phase').slice(0, 20), PHASE_COLORS[i % PHASE_COLORS.length]]).concat([['No phase', NO_PHASE_COLOR]])
        : [
          ['Not started', STATUS_COLOR.not_started],
          ['In progress', STATUS_COLOR.in_progress],
          ['Done', STATUS_COLOR.done],
          ['Blocked', STATUS_COLOR.blocked]
        ];
  const prios = Object.keys(PRIORITY_LABEL).map((k) => [PRIORITY_LABEL[k], PRIORITY_COLOR[k]]);
  let lx = 150;
  doc.setFontSize(7.5);
  doc.setTextColor(90, 100, 115);
  const keyTitle = colorBy === 'brand' ? 'Bar colour' : colorBy === 'priority' ? 'Priority (bars)' : colorBy === 'phase' ? 'Phase (bars)' : 'Status';
  doc.text(keyTitle, lx, 30);
  lx += doc.getTextWidth(keyTitle) + 4;
  legend.forEach(([label, col]) => {
    doc.setFillColor(col[0], col[1], col[2]);
    doc.rect(lx, 27, 3, 3, 'F');
    doc.text(label, lx + 4, 30);
    lx += 4 + doc.getTextWidth(label) + 6;
  });
  if (colorBy !== 'priority' && colorBy !== 'brand') {
    doc.text('Priority', lx + 2, 30);
    lx += 16;
    prios.forEach(([label, col]) => {
      doc.setFillColor(col[0], col[1], col[2]);
      doc.circle(lx + 1.5, 28.5, 1.5, 'F');
      doc.text(label, lx + 4, 30);
      lx += 4 + doc.getTextWidth(label) + 6;
    });
  }
  // milestone marker — same colour rules as the bars
  const markerCol = colorBy === 'brand' ? BRAND_GREEN : colorBy === 'priority' ? PRIORITY_COLOR.moderate : colorBy === 'phase' ? PHASE_COLORS[0] : STATUS_COLOR.not_started;
  doc.setFillColor(markerCol[0], markerCol[1], markerCol[2]);
  doc.triangle(lx + 2, 30, lx + 5, 30, lx + 3.5, 27, 'F');
  doc.text('By this date', lx + 7, 30);

  // ── table ──
  const groups = groupTasks(phases, tasks);

  // Which colour does each bar/diamond get? (mirrors the on-screen Gantt)
  const phaseIdx = {};
  phases.forEach((p, i) => { phaseIdx[p.id] = i; });
  const colorForTask = (t) => {
    if (colorBy === 'brand') return BRAND_GREEN;
    if (colorBy === 'priority') return PRIORITY_COLOR[t.priority || 'moderate'] || PRIORITY_COLOR.moderate;
    if (colorBy === 'phase') {
      const i = phaseIdx[t.phase_id];
      return i === undefined ? NO_PHASE_COLOR : PHASE_COLORS[i % PHASE_COLORS.length];
    }
    return STATUS_COLOR[t.status || 'not_started'] || STATUS_COLOR.not_started;
  };

  const baseHead = ['Phase / Task', 'Owner', 'Priority', 'Status', 'Start', 'End'];
  const baseW = 70 + 34 + 18 + 22 + 18 + 18; // fixed left columns (mm)
  const usableW = pageW - 24;                // 12mm margins
  // The whole project, day-by-day for short jobs, fitted across the page width.
  const { gran, cols, colWidths, pxPerDay } = calendarFor(project, phases, tasks, usableW, baseW);
  const colW = (colWidths.length ? colWidths.reduce((a, b) => a + b, 0) : 0) / Math.max(cols.length, 1);
  const colFont = colW < 4.5 ? 5 : colW < 9 ? 5.5 : cols.length > 16 ? 6 : 7.5;
  const bucketCols = {};
  cols.forEach((c, i) => { bucketCols[baseHead.length + i] = { cellWidth: colWidths[i], halign: 'center' }; });

  const head = [cols.length ? [...baseHead, ...cols.map((c, i) => bucketLabel(c, i === 0))] : baseHead];

  const body = [];
  const rowMeta = [];
  groups.forEach((g) => {
    body.push([pdfSafe(g.name), '', '', '', '', '', ...cols.map(() => '')]);
    rowMeta.push({ type: 'phase' });
    g.tasks.forEach((t) => {
      const row = [
        // no "◆ " prefix — the PDF draws the diamond (the font can't print ◆)
        pdfSafe(t.name),
        pdfSafe(ownerLabel(t)),
        PRIORITY_LABEL[t.priority || 'moderate'] || 'Moderate',
        STATUS_LABEL[t.status || 'not_started'] || 'Not started',
        t.start_date ? pretty(t.start_date) : 'TBC',
        t.end_date ? pretty(t.end_date) : 'TBC'
      ];
      if (cols.length) cols.forEach(() => row.push(''));
      body.push(row);
      rowMeta.push({ type: 'task', status: t.status || 'not_started', task: t });
    });
  });

  autoTable(doc, {
    head,
    body,
    startY: 41,
    theme: 'grid',
    // Very fine, very light grid — a heavy grid kills the calendar look.
    styles: { fontSize: colFont, cellPadding: 0.9, overflow: 'linebreak', textColor: [40, 50, 65], lineWidth: 0.05, lineColor: [226, 232, 240] },
    headStyles: { fillColor: [10, 25, 41], textColor: [255, 255, 255], fontSize: colFont, halign: 'center', lineWidth: 0.05, lineColor: [10, 25, 41] },
    columnStyles: {
      0: { cellWidth: 70, halign: 'left' },
      1: { cellWidth: 34 },
      2: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 22, halign: 'center' },
      4: { cellWidth: 18, halign: 'center' },
      5: { cellWidth: 18, halign: 'center' },
      ...bucketCols // the calendar, fitted across the printable width
    },
    didParseCell: (data) => {
      const meta = rowMeta[data.row.index];
      if (!meta) return;
      if (meta.type === 'phase') {
        data.cell.styles.fillColor = [232, 236, 239];
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.textColor = [10, 25, 41];
        return;
      }
      // Milestone rows get space for the drawn ◆ before the name.
      if (data.section === 'body' && data.column.index === 0 && meta.task && isMilestone(meta.task)) {
        data.cell.styles.cellPadding = { top: 0.9, right: 0.9, bottom: 0.9, left: 4.4 };
      }
      // Calendar cells must stay TRANSPARENT: the bar is drawn on top in
      // didDrawCell, and any later cell fill would paint over it.
      if (data.section === 'body' && data.column.index >= baseHead.length && meta.task) {
        data.cell.styles.fillColor = false;
      }
      if (data.column.index === 3 && meta.status === 'blocked') {
        data.cell.styles.textColor = [239, 68, 68];
        data.cell.styles.fontStyle = 'bold';
      }
    },
    didDrawCell: (data) => {
      const meta = rowMeta[data.row.index];
      if (!meta || meta.type !== 'task' || !meta.task) return;
      if (data.section !== 'body') return;
      const t = meta.task;

      // Milestone rows: draw a real ◆ marker before the task name.
      if (data.column.index === 0 && isMilestone(t)) {
        const mc = colorForTask(t);
        const mx = data.cell.x + 2.4;
        const my = data.cell.y + data.cell.height / 2;
        const mr = 1.35;
        doc.setFillColor(mc[0], mc[1], mc[2]);
        doc.triangle(mx, my - mr, mx + mr, my, mx, my + mr, 'F');
        doc.triangle(mx, my - mr, mx - mr, my, mx, my + mr, 'F');
        return;
      }

      // Draw the whole bar once, from the row's FIRST calendar cell.
      if (data.column.index !== baseHead.length) return;
      const s = t.start_date || t.end_date;
      const e = t.end_date || t.start_date;
      if (!s || !pxPerDay) return;

      const c = colorForTask(t);
      const firstX = data.cell.x;
      const cy = data.cell.y + data.cell.height / 2;

      // Milestone → a ◆ diamond sitting on its date.
      if (isMilestone(t)) {
        const d = diffDays(cols[0].start, s);
        const cx = Math.min(Math.max(firstX + (d + 0.5) * pxPerDay, firstX + 1), firstX + usableW - baseW - 1);
        const r = 1.6;
        doc.setFillColor(c[0], c[1], c[2]);
        doc.triangle(cx, cy - r, cx + r, cy, cx, cy + r, 'F');
        doc.triangle(cx, cy - r, cx - r, cy, cx, cy + r, 'F');
        return;
      }

      // Bar → rounded rectangle spanning start → end (clamped to the calendar).
      const barH = Math.min(Math.max(data.cell.height * 0.62, 2.6), 4.6);
      const x1 = Math.max(firstX + diffDays(cols[0].start, s) * pxPerDay, firstX);
      const x2 = Math.min(firstX + (diffDays(cols[0].start, e) + 1) * pxPerDay, firstX + usableW - baseW);
      const w = Math.max(x2 - x1, 1.4);
      doc.setFillColor(c[0], c[1], c[2]);
      doc.roundedRect(x1, cy - barH / 2, w, barH, barH / 2, barH / 2, 'F');
    },
    didDrawPage: () => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      doc.setTextColor(140, 148, 160);
      const footer = [company.company_name, company.company_website, company.company_reg_number ? `Reg ${company.company_reg_number}` : null, company.company_vat_number ? `VAT ${company.company_vat_number}` : null]
        .filter(Boolean).join('  ·  ');
      doc.text(footer, 12, pageH - 6);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - 12, pageH - 6, { align: 'right' });
    }
  });

  const stamp = new Date().toISOString().slice(0, 10);
  const safe = (project.name || 'Schedule').replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
  doc.save(`${project.project_number || 'Project'}_${safe}_${stamp}.pdf`);
}

// ============================================
// EXCEL — formatted .xls grid (opens straight in Excel, keeps colours)
// ============================================
const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export async function exportGanttExcel({ project = {}, phases = [], tasks = [], company: companyIn, colorBy = 'status' }) {
  const company = companyIn || await getCompanyDetails().catch(() => ({}));
  // Same calendar model as the PDF (whole project, day/week/month columns).
  const raw = rawSpan(project, tasks, phases);
  const span = alignSpan(raw, granularityFor(raw));
  const weeks = bucketsOf(span, granularityFor(raw));
  const groups = groupTasks(phases, tasks);

  // Same colour rules as the Gantt/PDF.
  const phaseIdx = {};
  phases.forEach((p, i) => { phaseIdx[p.id] = i; });
  const colorForTask = (t) => {
    if (colorBy === 'brand') return BRAND_GREEN;
    if (colorBy === 'priority') return PRIORITY_COLOR[t.priority || 'moderate'] || PRIORITY_COLOR.moderate;
    if (colorBy === 'phase') {
      const i = phaseIdx[t.phase_id];
      return i === undefined ? NO_PHASE_COLOR : PHASE_COLORS[i % PHASE_COLORS.length];
    }
    return STATUS_COLOR[t.status || 'not_started'] || STATUS_COLOR.not_started;
  };

  const headerCells = ['Phase / Task', 'Owner', 'Priority', 'Status', 'Start', 'End',
    ...weeks.map((w, i) => bucketLabel(w, i === 0))]
    .map((h) => `<th style="background:#0A1929;color:#fff;border:1px solid #ccc;font-size:10pt;">${esc(h)}</th>`)
    .join('');

  const rows = [];
  groups.forEach((g) => {
    rows.push(`<tr>
      <td colspan="${6 + weeks.length}" style="background:#E8ECEF;font-weight:bold;color:#0A1929;border:1px solid #ccc;">${esc(g.name)}</td>
    </tr>`);
    g.tasks.forEach((t) => {
      const st = t.status || 'not_started';
      const rgb = colorForTask(t);
      const cells = [
        esc(isMilestone(t) ? `◆ ${t.name}` : t.name),
        esc(ownerLabel(t)),
        esc(PRIORITY_LABEL[t.priority || 'moderate'] || 'Moderate'),
        esc(STATUS_LABEL[st] || 'Not started'),
        esc(t.start_date || 'TBC'),
        esc(t.end_date || 'TBC')
      ].map((v) => `<td style="border:1px solid #ddd;">${v}</td>`);

      weeks.forEach((w) => {
        const on = overlaps(t, w);
        cells.push(`<td style="border:1px solid #ddd;background:${on ? `rgb(${rgb.join(',')})` : '#ffffff'};"></td>`);
      });

      rows.push(`<tr>${cells.join('')}</tr>`);
    });
  });

  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel">
    <head><meta charset="utf-8" />
    <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
      <x:Name>Schedule</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
    </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
    <style>table{border-collapse:collapse;} td,th{padding:4px 6px;font-family:Arial;font-size:10pt;mso-number-format:"\\@";}</style>
    </head><body>
      <table>
        <tr><td colspan="${6 + weeks.length}" style="font-size:14pt;font-weight:bold;color:#0A1929;">${esc(company.company_name || 'One Purpose Logistics')}</td></tr>
        <tr><td colspan="${6 + weeks.length}" style="font-size:11pt;font-weight:bold;">${esc(project.name || 'Project Schedule')}</td></tr>
        <tr><td colspan="${6 + weeks.length}" style="color:#555;font-size:9pt;">
          ${esc([project.project_number, project.client?.company_name ? `Client: ${project.client.company_name}` : null, `Generated ${new Date().toLocaleDateString('en-ZA')}`].filter(Boolean).join('  ·  '))}
        </td></tr>
        <tr><td colspan="${6 + weeks.length}" style="font-size:9pt;color:#777;">
          Reg ${esc(company.company_reg_number || '')} · VAT ${esc(company.company_vat_number || '')} · ${esc(company.company_website || '')}
        </td></tr>
        <tr><td colspan="${6 + weeks.length}" style="font-size:9pt;color:#333;">
          ${esc(`${tasks.length} tasks · ${tasks.filter((t) => t.status === 'done').length} done · ${tasks.filter((t) => t.status === 'in_progress').length} in progress · ${tasks.filter((t) => t.status === 'blocked').length} blocked · ${tasks.filter(isMilestone).length} milestones`)}
        </td></tr>
        <tr><td colspan="${6 + weeks.length}" style="font-size:8pt;color:#666;">
          Key — Status: Not started / In progress / Done / Blocked &nbsp;·&nbsp; Priority: Urgent / High / Moderate / Low &nbsp;·&nbsp; ◆ = by-this-date milestone
        </td></tr>
        <tr>${headerCells}</tr>
        ${rows.join('')}
      </table>
    </body></html>`;

  const blob = new Blob(['\ufeff' + html], { type: 'application/vnd.ms-excel;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const safe = (project.name || 'Schedule').replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
  link.href = url;
  link.download = `${project.project_number || 'Project'}_${safe}.xls`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
